import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, ListItemText, MenuItem,
  OutlinedInput, Select, Stack, TextField, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import type { UserDetail } from '../../api/types';
import {
  selectableSites, useEditUser, useMyPermissions, useRolesList, useSetUserRoles, useSetUserSites, useSitesList,
} from '../../api/queries';
import { COMMON_TIMEZONES, timeZoneLabel } from '../../utils/timezones';

/**
 * Edit an existing user — profile, roles, and sites in one dialog.
 *
 * Roles/sites are V8-flatten replace-set semantics: the dialog displays
 * the current selection and a Save commits the entire desired set in
 * one PUT each. Empty site selection means "all operator sites" (the
 * "no narrowing" default).
 *
 * Cross-operator edits are 403 server-side; the dialog respects the
 * caller's tenancy by only listing roles/sites the user could legally
 * be granted (operator-tier roles for the user's operator + that
 * operator's sites).
 */
export type EditUserDialogProps = {
  open: boolean;
  user: UserDetail | null;
  onClose: () => void;
};

export function EditUserDialog({ open, user, onClose }: EditUserDialogProps) {
  const edit      = useEditUser(user?.id ?? '');
  const setRoles  = useSetUserRoles();
  const setSites  = useSetUserSites();
  const me        = useMyPermissions();
  const has       = (code: string) => !!me.data?.codes?.includes(code);

  // Pass the user's operator so the API surfaces that operator's
  // clones (otherwise ACES sees only Platform-tier + templates and the
  // dropdown is empty for operator users — V9 tenancy escape hatch).
  const rolesList = useRolesList(user?.operatorId ?? null);
  const rolesQ    = useQuery({ queryKey: rolesList.queryKey, queryFn: rolesList.listFn, staleTime: 60_000 });
  const sitesList = useSitesList();
  const sitesQ    = useQuery({ queryKey: sitesList.queryKey, queryFn: sitesList.listFn, staleTime: 60_000 });

  const [username, setUsername]       = useState('');
  const [email, setEmail]             = useState('');
  const [displayName, setDisplayName] = useState('');
  const [timeZone, setTimeZone]       = useState('America/New_York');
  const [phone, setPhone]             = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);

  useEffect(() => {
    if (open && user) {
      setUsername(user.username);
      setEmail(user.email ?? '');
      setDisplayName(user.displayName);
      setTimeZone(user.timeZone);
      setPhone(user.phone ?? '');
      setSelectedRoleIds(user.roles.map((r) => r.id));
      setSelectedSiteIds(user.siteIds);
      edit.reset(); setRoles.reset(); setSites.reset();
    }
    // The mutation refs are unstable across renders; including them
    // would loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  // V9 tenancy: ACES users hold Platform-tier roles only — templates
  // (Tier=Operator, OperatorId=NULL) exist solely for cloning into
  // operators and are not user-assignable. Operator users hold their
  // own operator's clones. System users (Ed25519 service accounts) are
  // platform-wide too — same role surface as ACES (e.g. ReleasesPublisher).
  const availableRoles = useMemo(() => {
    if (!user) return [];
    return (rolesQ.data ?? []).filter((r) => {
      if (!r.isActive) return false;
      if (user.isArms || user.isSystem) return r.tier === 'Platform';
      return r.tier === 'Operator' && r.operatorId === user.operatorId;
    });
  }, [rolesQ.data, user]);

  // Sites available to narrow on: only the user's operator's sites.
  // Platform-tier users (ACES + System) have no operator and therefore
  // no site-scoping — they see everything.
  const availableSites = useMemo(() => {
    if (!user || user.isArms || user.isSystem) return [];
    return selectableSites(sitesQ.data).filter((s) => s.operatorId === user.operatorId);
  }, [sitesQ.data, user]);

  if (!user) return null;

  const usernameOk = /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/.test(username.trim());
  const emailOk    = email.trim() === '' || /.+@.+\..+/.test(email.trim());
  const nameOk     = displayName.trim().length > 0;
  const formOk     = usernameOk && emailOk && nameOk;

  const rolesChanged = !sameSet(selectedRoleIds, user.roles.map((r) => r.id));
  const sitesChanged = !sameSet(selectedSiteIds, user.siteIds);

  async function save() {
    if (!user) return;

    // Profile diff — only PATCH fields the admin actually changed so the
    // before/after audit stays signal-rich.
    const patch: Parameters<ReturnType<typeof useEditUser>['mutateAsync']>[0] = {};
    if (displayName.trim() !== user.displayName) patch.displayName = displayName.trim();
    if (username.trim() !== user.username)       patch.username    = username.trim();
    if ((email.trim() || '') !== (user.email ?? '')) patch.email   = email.trim();
    if (timeZone !== user.timeZone)              patch.timeZone    = timeZone;
    if ((phone.trim() || '') !== (user.phone ?? '')) patch.phone   = phone.trim() || null;
    if (Object.keys(patch).length > 0) await edit.mutateAsync(patch);

    if (rolesChanged && has('users.edit')) {
      await setRoles.mutateAsync({ userId: user.id, roleIds: selectedRoleIds });
    }
    if (sitesChanged && has('users.edit')) {
      await setSites.mutateAsync({ userId: user.id, siteIds: selectedSiteIds });
    }
    onClose();
  }

  const error = edit.error ?? setRoles.error ?? setSites.error;
  const isPending = edit.isPending || setRoles.isPending || setSites.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Edit user
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="overline" color="text.secondary">Profile</Typography>
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required fullWidth size="small"
            error={username !== '' && !usernameOk}
            helperText={username !== '' && !usernameOk
              ? '3–64 chars: letters, digits, dot, underscore, dash. Must start with letter or digit.'
              : 'Globally unique. Case-insensitive at sign-in.'}
          />
          <TextField
            label="Email (optional)" type="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            fullWidth size="small"
            error={email !== '' && !emailOk}
            helperText={email !== '' && !emailOk ? 'Must look like an email address.' : 'Used for sign-in fallback and notifications.'}
          />
          <TextField
            label="Display name" value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required fullWidth size="small"
          />
          <TextField
            select label="Time zone" value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            fullWidth size="small"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <MenuItem key={tz} value={tz}>{timeZoneLabel(tz)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Phone (optional)" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth size="small"
          />

          <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Roles</Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id="edit-user-roles-label">Roles</InputLabel>
            <Select
              labelId="edit-user-roles-label"
              multiple
              value={selectedRoleIds}
              onChange={(e) => setSelectedRoleIds(typeof e.target.value === 'string'
                ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((rid) => {
                    const r = availableRoles.find((x) => x.id === rid);
                    return <Chip key={rid} size="small" label={r?.displayName ?? rid.slice(0, 8)} />;
                  })}
                </Box>
              )}
            >
              {availableRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  <Checkbox checked={selectedRoleIds.includes(r.id)} />
                  <ListItemText primary={r.displayName} secondary={`${r.code} · ${r.tier}`} />
                </MenuItem>
              ))}
              {availableRoles.length === 0 && (
                <MenuItem disabled>No roles available for this user.</MenuItem>
              )}
            </Select>
          </FormControl>

          {!user.isArms && !user.isSystem && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
                Sites (leave empty for all operator sites)
              </Typography>
              <FormControl size="small" fullWidth>
                <InputLabel id="edit-user-sites-label">Sites</InputLabel>
                <Select
                  labelId="edit-user-sites-label"
                  multiple
                  value={selectedSiteIds}
                  onChange={(e) => setSelectedSiteIds(typeof e.target.value === 'string'
                    ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="Sites" />}
                  renderValue={(selected) => (selected as string[]).length === 0
                    ? <em style={{ color: 'rgba(0,0,0,0.5)' }}>All operator sites</em>
                    : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((sid) => {
                          const s = availableSites.find((x) => x.id === sid);
                          return <Chip key={sid} size="small" label={s?.displayName ?? sid.slice(0, 8)} />;
                        })}
                      </Box>
                    )}
                >
                  {availableSites.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Checkbox checked={selectedSiteIds.includes(s.id)} />
                      <ListItemText primary={s.displayName} secondary={s.code} />
                    </MenuItem>
                  ))}
                  {availableSites.length === 0 && (
                    <MenuItem disabled>No sites configured for this operator yet.</MenuItem>
                  )}
                </Select>
              </FormControl>
            </>
          )}

          {error && (
            <ErrorAlert>
              {(error as Error).message
                || (error as { code?: string }).code
                || String(error)
                || 'Save failed.'}
            </ErrorAlert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button variant="contained" disabled={!formOk || isPending} onClick={save}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function sameSet(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  for (const x of b) if (!set.has(x)) return false;
  return true;
}
